import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { Wordmark, LogoIcon } from "@/components/Wordmark";
import { useTheme } from "@/theme/ThemeProvider";

export default function AuthPage() {
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState(() => localStorage.getItem("edge_remembered_email") || "");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [remember, setRemember] = useState(() => !!localStorage.getItem("edge_remembered_email"));
  const [acceptedTos, setAcceptedTos] = useState(false);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => { if (user) navigate("/"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && !acceptedTos) {
      toast.error("Please accept the Terms of Service");
      return;
    }
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        if (remember) localStorage.setItem("edge_remembered_email", email);
        else localStorage.removeItem("edge_remembered_email");
        toast.success("WELCOME BACK");
      } else {
        await register(email, password, name);
        if (remember) localStorage.setItem("edge_remembered_email", email);
        toast.success("ACCOUNT ARMED");
      }
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 relative" style={{ background: "var(--bg)", color: "var(--fg)", fontFamily: "var(--font-body)" }}>
      {/* Aurora background matching the app shell */}
      <div
        aria-hidden
        className="fixed inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse 900px 500px at 8% 5%, rgba(34,197,94,0.10), transparent 60%),
            radial-gradient(ellipse 900px 500px at 92% 105%, rgba(168,85,247,0.14), transparent 60%),
            radial-gradient(ellipse 1200px 700px at 50% 50%, rgba(168,85,247,0.03), transparent 70%)
          `,
        }}
      />
      {/* Left visual */}
      <div className="relative hidden lg:flex flex-col justify-between p-10 overflow-hidden" style={{ borderRight: "1px solid var(--border)" }}>
        <div
          aria-hidden
          className="absolute -top-24 -right-24 w-[500px] h-[500px] rounded-full opacity-40 blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(168,85,247,0.35), transparent 70%)" }}
        />
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.025] pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(circle, rgba(255,255,255,1) 1px, transparent 1px)",
            backgroundSize: "24px 24px",
          }}
        />

        <div className="relative z-10">
          <Wordmark theme={theme} size="lg" />
        </div>

        <div className="relative z-10 max-w-md">
          <div className="text-xs font-medium mb-4" style={{ color: "var(--accent)" }}>Trading journal · Coach · 7-day free trial</div>
          <h1 className="text-5xl xl:text-6xl font-semibold leading-[1.05]" style={{ fontFamily: "var(--font-heading)", letterSpacing: "-0.04em" }}>
            Sharpen the<br/>
            <span style={{ background: "var(--accent-gradient)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>edge</span> that<br/>
            actually pays.
          </h1>
          <p className="mt-6 text-sm leading-relaxed max-w-sm" style={{ color: "var(--fg-2)" }}>
            A trading journal built for operators. Upload your CSV, dissect every fill, and let an AI coach
            tell you the truth your P&amp;L is hiding.
          </p>

          <div className="mt-10 grid grid-cols-3 gap-3">
            {[
              { k: "P&L", v: "+$12,847", accent: "green" },
              { k: "WIN RATE", v: "62.4%", accent: "purple" },
              { k: "PF", v: "2.18", accent: "purple" },
            ].map((s) => (
              <div key={s.k} className="p-4" style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "12px", backdropFilter: "blur(12px)" }}>
                <div className="text-[10px] font-medium tracking-wider" style={{ color: "var(--fg-3)" }}>{s.k}</div>
                <div className="text-lg font-semibold mt-1" style={{ color: s.accent === "green" ? "var(--green)" : "var(--fg)", fontFamily: "var(--font-heading)", fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em" }}>{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-[10px] font-medium tracking-wider" style={{ color: "var(--fg-3)" }}>© 2026 · TradeGreen v2</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8 relative">
        <div className="w-full max-w-sm">
          <div className="text-xs font-medium" style={{ color: "var(--accent)" }}>{mode === "login" ? "Authenticate" : "Register"}</div>
          <h2 className="text-3xl font-semibold mt-2 mb-8 flex items-center gap-3" data-testid="auth-title" style={{ fontFamily: "var(--font-heading)", letterSpacing: "-0.03em" }}>
            <LogoIcon size={30} />
            <span>{mode === "login" ? "Sign in to TradeGreen." : "Create your TradeGreen account."}</span>
          </h2>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="text-xs font-medium block mb-1.5" style={{ color: "var(--fg-3)" }}>Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="auth-name-input"
                  className="w-full px-3 py-2.5 text-sm focus:outline-none transition-colors"
                  style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "10px", color: "var(--fg)", backdropFilter: "blur(12px)" }}
                  placeholder="Jane Trader"
                />
              </div>
            )}
            <div>
              <label className="text-xs font-medium block mb-1.5" style={{ color: "var(--fg-3)" }}>Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="auth-email-input"
                className="w-full px-3 py-2.5 text-sm focus:outline-none transition-colors"
                style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "10px", color: "var(--fg)", fontVariantNumeric: "tabular-nums", backdropFilter: "blur(12px)" }}
                placeholder="trader@desk.io"
              />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1.5" style={{ color: "var(--fg-3)" }}>Password</label>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="auth-password-input"
                className="w-full px-3 py-2.5 text-sm focus:outline-none transition-colors"
                style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "10px", color: "var(--fg)", fontVariantNumeric: "tabular-nums", backdropFilter: "blur(12px)" }}
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="auth-submit-button"
              className="w-full transition-all px-4 py-3 text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ background: "var(--accent-gradient)", color: "#FAFAFA", borderRadius: "12px", boxShadow: "var(--accent-glow)" }}
            >
              {loading ? "PROCESSING…" : (mode === "login" ? "SIGN IN" : "CREATE ACCOUNT")}
              <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </button>

            <label className="flex items-center gap-2 pt-1 cursor-pointer select-none group" data-testid="remember-me-label">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                data-testid="remember-me-checkbox"
                className="appearance-none w-3.5 h-3.5 border border-zinc-700 bg-zinc-900 checked:bg-emerald-400 checked:border-emerald-400 transition-colors cursor-pointer relative
                  checked:after:content-['✓'] checked:after:absolute checked:after:inset-0 checked:after:text-zinc-950 checked:after:text-[10px] checked:after:font-bold checked:after:flex checked:after:items-center checked:after:justify-center"
              />
              <span className="text-[11px] text-zinc-500 group-hover:text-zinc-300 transition-colors tracking-wide">REMEMBER ME ON THIS DEVICE</span>
            </label>

            {mode === "register" && (
              <label className="flex items-start gap-2 pt-1 cursor-pointer select-none group" data-testid="tos-label">
                <input
                  type="checkbox"
                  checked={acceptedTos}
                  onChange={(e) => setAcceptedTos(e.target.checked)}
                  data-testid="tos-checkbox"
                  className="appearance-none w-3.5 h-3.5 mt-0.5 border border-zinc-700 bg-zinc-900 checked:bg-emerald-400 checked:border-emerald-400 transition-colors cursor-pointer relative shrink-0
                    checked:after:content-['✓'] checked:after:absolute checked:after:inset-0 checked:after:text-zinc-950 checked:after:text-[10px] checked:after:font-bold checked:after:flex checked:after:items-center checked:after:justify-center"
                />
                <span className="text-[11px] text-zinc-500 group-hover:text-zinc-300 transition-colors leading-relaxed">
                  I have read and agree to the{" "}
                  <Link to="/terms" target="_blank" className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2" data-testid="tos-link">Terms of Service</Link>
                  , and I understand that <strong className="text-zinc-300">trading is risky, TradeGreen does not provide investment advice</strong>, and I am solely responsible for my own trading decisions.
                </span>
              </label>
            )}
          </form>

          <div className="mt-6 text-xs text-zinc-500">
            {mode === "login" ? "New trader?" : "Already have an account?"}{" "}
            <button
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              data-testid="auth-toggle-button"
              className="text-emerald-400 hover:text-emerald-300 underline-offset-4 hover:underline"
            >
              {mode === "login" ? "Create one →" : "Sign in →"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
